import React from 'react';
import { withRouter } from 'react-router-dom';
import { get, isEmpty } from 'lodash';
import queryString from 'query-string';
import shortid from 'shortid';
import * as backRoute from 'utils/backRoute';

// local dependencies
import { DASHBOARD } from 'constants/route-paths';
import { MemberClaimDetailWidgetService } from 'services/memberClaimDetailWidget';
import { memberClaimDetail as MemberClaimDetailView } from 'ui/views/widgets/memberClaimDetail';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberClaimDetail } from 'ui/containers/MemberClaimDetail';
 *
 * // render
 * <MemberClaimDetail propName='propValue'/>
 */
export class _MemberClaimDetail extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // check if URL container `memberId` query parameter.
        const queryParams = queryString.parse( this.props.location.search );

        this.handleNavActionClick = this.handleNavActionClick.bind( this );

        this.state = {
            isFetching: true,
            networkStatus: undefined,
            memberId: get( this.props.location, 'state.memberId', undefined ),
            patientId: get( this.props.location, 'state.patientId', undefined ),
            claimId: get( queryParams, 'claimId' ),
            componentData: undefined,
            refreshKey: shortid.generate(),
        };

        // create service instance
        this.memberClaimDetailWidgetService = new MemberClaimDetailWidgetService();

        // previously if - `memberId` is not present, redirect to the dashboard landing
        // present if - `patientId` is not present, redirect to the dashboard landing
        // else - make service request for the component data
        if( isEmpty( this.state.patientId ) ||  isEmpty( this.state.claimId ) ) {
            this.props.history.replace( DASHBOARD );
        } else {
            this.fetchComponentData(); // start component data fetch
        }
    }

    /**
     * @desc: fetch widgte sequence list
     */
    async fetchComponentData() {
        log.debug( 'MemberClaimDetail.fetchComponentData()' );

        // cancel ongoing request
        this.memberClaimDetailWidgetService.cancel();

        // get status and service data
        const { status, data } = await this.memberClaimDetailWidgetService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                data: {},
                context: {
                    memberId: this.state.memberId,
                    patientId: this.state.patientId,
                    claimId: this.state.claimId,
                }
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    handleNavActionClick( { type, value } ) {
        log.debug( 'MemberClaimDetail.handleNavActionClick()' );

        if( 'headerAction' === type && 'back' === value.name ) {

            // get back-route payload
            const backRoutePayload = backRoute.decode( this.props.location );

            if( ! isEmpty( backRoutePayload ) ) {
                return this.props.history.push( backRoutePayload );
            }

            // go to back
            this.props.history.goBack();
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.memberClaimDetailWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberClaimDetailView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                memberId={ this.state.memberId }
                patientId={ this.state.patientId }
                refreshKey={ this.state.refreshKey }
                { ...get( this.state, 'componentData', {} ) }
                requestPayload = { {
                    context: {
                        memberId: this.state.memberId,
                        patientId: this.state.patientId,
                        claimId: this.state.claimId
                    }
                } }
                onNavActionClick={ this.handleNavActionClick }
            />
        );
    }
}

// set display name
_MemberClaimDetail.displayName = '_MemberClaimDetail';

// set default props
_MemberClaimDetail.defaultProps = {};

// prop types
_MemberClaimDetail.propTypes = {};

export const MemberClaimDetail = withRouter( _MemberClaimDetail );
